import { useParams, useNavigate } from "react-router-dom";
import { useGetMessageDetailQuery } from "../../../store/admin/communicationSlice";
import { CardComp, ButtonComp } from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import { Loading } from "../../../components";
import { getStatusBadgeColor } from "../../../utils/communicationUtils";
import { convertToTitleCase } from "../../../utils/helpers";
import moment from "moment";
import styled from "styled-components";

export const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: message, isLoading, error } = useGetMessageDetailQuery(id, {
    skip: !id,
  });

  if (isLoading) {
    return (
      <Wrapper className="mx-2 mx-lg-4">
        <PageTitle title={"Message detail"} />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <Loading />
        </div>
      </Wrapper>
    );
  }

  if (error || !message) {
    return (
      <Wrapper className="mx-2 mx-lg-4">
        <PageTitle title={"Message detail"} />
        <div className="alert alert-danger">
          Failed to load message. Please try again.
        </div>
        <ButtonComp
          btnText={"Back to history"}
          btnClassName={"py-2 px-3 mt-3"}
          onClick={() => navigate("/super_admin/communication")}
        />
      </Wrapper>
    );
  }

  const deliveryLogs = message?.delivery_logs || message?.deliveryLogs || [];
  const statusColor = getStatusBadgeColor(message?.status);

  return (
    <Wrapper className="mx-2 mx-lg-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none"
          onClick={() => navigate(-1)}
          style={{ color: "var(--color-primary)" }}
        >
          Back
        </button>
        <PageTitle title={message?.header || "Message detail"} />
      </div>

      <CardComp cardClassName={"mb-4"}>
        <div className="px-3 pb-4">
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <small className="text-muted d-block">Date</small>
              <span>{message?.created_at ? moment(message.created_at).format("DD-MMM-YYYY HH:mm") : "-"}</span>
            </div>
            <div className="col-12 col-md-6">
              <small className="text-muted d-block">Status</small>
              <span
                className="badge rounded-pill"
                style={{
                  backgroundColor: statusColor,
                  color: "#fff",
                  padding: "4px 10px",
                }}
              >
                {convertToTitleCase(message?.status || "Unknown")}
              </span>
            </div>
            <div className="col-12 col-md-6">
              <small className="text-muted d-block">Channel</small>
              <span>{convertToTitleCase((message?.sending_mode || "").replace("Mode", "") || "-")}</span>
            </div>
            <div className="col-12 col-md-6">
              <small className="text-muted d-block">Sent by</small>
              <span>
                {message?.created_by_staff
                  ? `${message.created_by_staff.first_name || ""} ${message.created_by_staff.last_name || ""}`.trim() ||
                    `Staff #${message.created_by}`
                  : message?.created_by
                  ? `Staff #${message.created_by}`
                  : "-"}
              </span>
            </div>
          </div>

          <hr />

          <div className="mb-4">
            <h6 className="fw-bold mb-2">Content</h6>
            <div
              className="p-3 rounded"
              style={{ backgroundColor: "#f7f7f7", border: "1px solid #e0e0e0" }}
            >
              {message?.content_email ? (
                <div dangerouslySetInnerHTML={{ __html: message.content_email }} />
              ) : (
                <p className="mb-0">{message?.body || "-"}</p>
              )}
            </div>
          </div>

          {deliveryLogs.length > 0 && (
            <>
              <h6 className="fw-bold mb-2">Delivery breakdown</h6>
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: "14px" }}>
                  <thead style={{ backgroundColor: "var(--color-primary-dark)", color: "#fff" }}>
                    <tr>
                      <th>Recipient ID</th>
                      <th>Type</th>
                      <th>Channel</th>
                      <th>Status</th>
                      <th>Delivered at</th>
                      <th>Failure reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryLogs.map((log) => (
                      <tr key={log?.id}>
                        <td>{log?.recipient_id ?? "-"}</td>
                        <td>{convertToTitleCase(log?.recipient_type || "-")}</td>
                        <td>{convertToTitleCase(log?.channel || "-")}</td>
                        <td>
                          <span
                            className="badge rounded-pill"
                            style={{
                              backgroundColor: getStatusBadgeColor(log?.status),
                              color: "#fff",
                              padding: "2px 8px",
                              fontSize: "12px",
                            }}
                          >
                            {convertToTitleCase(log?.status || "-")}
                          </span>
                        </td>
                        <td>
                          {log?.delivered_at
                            ? moment(log.delivered_at).format("DD-MMM-YYYY HH:mm")
                            : "-"}
                        </td>
                        <td>{log?.failure_reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </CardComp>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  .table th {
    font-weight: 600;
    padding: 10px;
  }
  .table td {
    padding: 10px;
    border-color: #e0e0e0;
  }
`;

export default MessageDetail;
